/**
 * @fileoverview Badge Component
 *
 * Small label component for displaying status, categories, or counts.
 * Supports multiple color variants for different contexts.
 *
 * @module components/Badge
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * Badge variant styles configuration
 *
 * @constant
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80 dark:text-primary-foreground',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        success:
          'border-transparent bg-success text-success-foreground hover:bg-success/80',
        warning:
          'border-transparent bg-warning text-warning-foreground hover:bg-warning/80',
        info: 'border-transparent bg-info text-info-foreground hover:bg-info/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        active:
          'bg-success/15 text-success dark:bg-success/20 dark:text-green-400 hover:bg-success/20 dark:hover:bg-success/25',
        trial:
          'bg-warning/15 text-warning dark:bg-warning/20 dark:text-amber-300 hover:bg-warning/20 dark:hover:bg-warning/25',
        suspended:
          'bg-destructive/15 text-destructive dark:bg-destructive/20 dark:text-red-400 hover:bg-destructive/20 dark:hover:bg-destructive/25',
        enterprise:
          'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 hover:bg-blue-100/80 dark:hover:bg-blue-900',
        professional:
          'bg-primary/15 text-primary dark:bg-blue-950/50 dark:text-blue-400 hover:bg-primary/20 dark:hover:bg-blue-950/70',
        basic:
          'bg-muted text-muted-foreground dark:bg-slate-800 dark:text-slate-300 hover:bg-muted/80',
        deprecated: 'bg-gray-500/10 text-gray-700 border-gray-300',
        inactive: 'bg-yellow-500/10 text-yellow-700 border-yellow-300',

        /* Roles */
        super_admin: 'bg-red-100 text-red-800',
        internal_admin: 'bg-purple-100 text-purple-800',
        company_admin: 'bg-blue-100 text-blue-800',
        end_user: 'bg-green-100 text-green-800',

        /* Apps */
        admin: 'bg-green-100 text-green-800',
        company: 'bg-blue-100 text-blue-800',
        'trueid-locker': 'bg-yellow-100 text-yellow-800',
        'estate-vault': 'bg-red-100 text-red-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * Props for the Badge component
 *
 * @interface BadgeProps
 */
interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Badge component for displaying status indicators, labels, or counts
 *
 * Small, pill-shaped component for highlighting information or status.
 * Supports multiple color variants for different semantic meanings.
 *
 * @component
 * @param {BadgeProps} props - Component props
 * @returns {JSX.Element} Rendered badge element
 *
 * @example
 * ```tsx
 * // Default primary badge
 * <Badge>New</Badge>
 *
 * // Success variant
 * <Badge variant="success">Active</Badge>
 *
 * // Destructive variant
 * <Badge variant="destructive">Critical</Badge>
 *
 * // Custom styling
 * <Badge className="ml-2" variant="info">
 *   {count} items
 * </Badge>
 * ```
 *
 * @remarks
 * Variants:
 * - default: Primary brand color
 * - secondary: Muted secondary color
 * - success: Green for positive status
 * - warning: Yellow/amber for warnings
 * - info: Blue for informational content
 * - destructive: Red for errors or dangerous actions
 * - outline: Bordered with no background
 * - active: Indicates active status
 * - trial: Indicates trial status
 * - suspended: Indicates suspended status
 * - enterprise: Enterprise plan badge
 * - professional: Professional plan badge
 * - basic: Basic plan badge
 * - deprecated: Deprecated status badge
 * - inactive: Inactive status badge
 * - super_admin: Super admin role badge
 * - internal_admin: Internal admin role badge
 * - company_admin: Company admin role badge
 * - end_user: End user role badge
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  ),
);
Badge.displayName = 'Badge';

export { Badge, type BadgeProps };
